import {
  type AllIconKeys,
  Modal as Biom3Modal,
  Box,
  ButtCon,
  Divider,
  type SxProps,
  Toolbar,
  isDualVariantIcon,
} from "@biom3/react";
import {
  Children,
  type ReactNode,
  isValidElement,
  useCallback,
  useMemo,
} from "react";

import { ModalActions } from "@/components/modal/ModalActions";
import { ModalContent } from "@/components/modal/ModalContent";
import { ModalContentLayout } from "@/components/modal/ModalContentLayout";
import { MODAL_WIDTH } from "@/constants";

type ModalComponents = {
  modalContent: ReactNode;
  modalActions: ReactNode;
};

export function Modal({
  title,
  open,
  children,
  onSubmit,
  rightButtCon,
  leftButtCon,
  onRightButtonClick,
  onLeftButtonClick,
  onCloseModal,
  testId,
  outsideClicksClose = true,
  divider = true,
  width = {},
  closeButtonPadding,
  buttonVariant,
  sx,
}: {
  title?: string;
  children?: ReactNode;
  onSubmit?: () => void;
  open: boolean;
  rightButtCon?: AllIconKeys;
  leftButtCon?: AllIconKeys;
  testId?: string;
  onRightButtonClick?: () => void;
  onLeftButtonClick?: () => void;
  onCloseModal?: () => void;
  outsideClicksClose?: boolean;
  divider?: boolean;
  width?: {
    default?: string;
    medium?: string;
    large?: string;
  };
  closeButtonPadding?: string;
  buttonVariant?: "primary" | "secondary" | "tertiary";
  sx?: SxProps;
}) {
  const { modalContent, modalActions } = useMemo<ModalComponents>(() => {
    let modalContent: ReactNode = null;
    let modalActions: ReactNode = null;
    Children.forEach(children, (child) => {
      if (!isValidElement(child)) return;
      switch (child.type) {
        case ModalContent: {
          modalContent = child;
          break;
        }
        case ModalActions: {
          modalActions = child;
          break;
        }
      }
    });
    return { modalContent, modalActions };
  }, [children]);

  const handleRightButtonClick = useCallback(() => {
    onRightButtonClick?.();
    onCloseModal?.();
  }, [onRightButtonClick, onCloseModal]);

  return (
    <Biom3Modal
      visible={open}
      outsideClicksClose={outsideClicksClose}
      onCloseModal={onCloseModal}
      testId={testId}
    >
      <Biom3Modal.Content
        sx={{
          d: "flex",
          flexDirection: "column",
          background: "var(--on-dark-base-color-neutrals-500, #1F1F1F)",
          borderRadius: "base.borderRadius.x8",
          boxShadow: "base.shadow.400",
          width: {
            default: "calc(100dvw - 32px)",
            medium: MODAL_WIDTH,
            large: "480px",
            ...width,
          },
          height: "auto",
          ...sx,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            flexDirection: "column",
            width: "100%",
            overflow: "auto",
          }}
        >
          {(title || rightButtCon || leftButtCon) && (
            <Toolbar
              testId="modal--toolbar"
              sx={{
                background: "var(--on-dark-base-color-neutrals-500, #1F1F1F)",
                p: closeButtonPadding,
              }}
            >
              {leftButtCon && (
                <Toolbar.LeftButtCon
                  variant="tertiary"
                  onClick={onLeftButtonClick}
                  {...(isDualVariantIcon(leftButtCon)
                    ? { icon: leftButtCon }
                    : { icon: leftButtCon })}
                />
              )}
              <Toolbar.Title
                sx={{
                  wordBreak: "break-word",
                  WebkitLineClamp: 2,
                }}
              >
                {title}
              </Toolbar.Title>
              {rightButtCon && (
                <Toolbar.RightSlot>
                  <ButtCon
                    onClick={handleRightButtonClick}
                    variant={buttonVariant}
                    {...(isDualVariantIcon(rightButtCon)
                      ? { icon: rightButtCon }
                      : { icon: rightButtCon })}
                  />
                </Toolbar.RightSlot>
              )}
            </Toolbar>
          )}
          {divider && (
            <Divider
              size="small"
              sx={{
                ":after": {
                  bg: "base.color.neutral.600",
                },
              }}
            />
          )}
          <Box
            rc={onSubmit ? <form onSubmit={onSubmit} /> : <div />}
            sx={{ width: "100%", overflowX: "auto" }}
          >
            {modalContent}
            {modalActions}
          </Box>
        </Box>
      </Biom3Modal.Content>
    </Biom3Modal>
  );
}

Modal.Content = ModalContent;
Modal.Actions = ModalActions;
Modal.ContentLayout = ModalContentLayout;
